import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  LockKeyhole,
  Plus,
  RefreshCcw,
  Save,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import {
  normalizeMachineTypeInput,
  type MachineFieldType,
  type MachinesGateway,
  type MachineTypeAdminRecord,
  type SaveMachineTypeFieldInput,
} from '@forq/machines-data';

interface MachineTypesFeatureProps {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly gateway: MachinesGateway;
  readonly canManage: boolean;
}

interface FieldDraft {
  readonly localId: string;
  readonly id?: string;
  readonly key: string;
  readonly label: string;
  readonly type: MachineFieldType;
  readonly unit: string;
  readonly required: boolean;
  readonly optionsText: string;
  readonly active: boolean;
  readonly group: string;
}

interface TypeDraft {
  readonly machineTypeId?: string;
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly active: boolean;
  readonly fields: readonly FieldDraft[];
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Maschinentypen konnten nicht gespeichert werden.';
}

function draftFrom(record: MachineTypeAdminRecord): TypeDraft {
  return {
    machineTypeId: record.id,
    code: record.code,
    name: record.name,
    description: record.description,
    active: record.active,
    fields: record.fields.map((field) => ({
      localId: field.id,
      id: field.id,
      key: field.key,
      label: field.label,
      type: field.type,
      unit: field.unit,
      required: field.required,
      optionsText: field.options.join(', '),
      active: field.active,
      group: field.group ?? '',
    })),
  };
}

function blankDraft(): TypeDraft {
  return {
    code: '',
    name: '',
    description: '',
    active: true,
    fields: [],
  };
}

function toFieldInput(field: FieldDraft): SaveMachineTypeFieldInput {
  return {
    ...(field.id ? { id: field.id } : {}),
    key: field.key,
    label: field.label,
    type: field.type,
    unit: field.unit,
    required: field.required,
    options: field.type === 'select' || field.type === 'multiselect'
      ? field.optionsText.split(',').map((option) => option.trim()).filter(Boolean)
      : [],
    active: field.active,
    group: field.group,
  };
}

export function MachineTypesFeature({
  tenantId,
  tenantName,
  gateway,
  canManage,
}: MachineTypesFeatureProps) {
  const [catalog, setCatalog] = useState<readonly MachineTypeAdminRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<TypeDraft>(blankDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRecord = useMemo(
    () => catalog.find((item) => item.id === selectedId) ?? null,
    [catalog, selectedId],
  );

  const selectRecord = useCallback((record: MachineTypeAdminRecord) => {
    setSelectedId(record.id);
    setDraft(draftFrom(record));
    setError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await gateway.listMachineTypesForAdmin(tenantId);
      setCatalog(next);
      const selected = next[0] ?? null;
      if (selected) {
        setSelectedId(selected.id);
        setDraft(draftFrom(selected));
      } else {
        setSelectedId('new');
        setDraft(blankDraft());
      }
    } catch (loadError) {
      setCatalog([]);
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateDraft = useCallback(<Key extends keyof TypeDraft,>(key: Key, value: TypeDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const updateField = useCallback((localId: string, patch: Partial<FieldDraft>) => {
    setDraft((current) => ({
      ...current,
      fields: current.fields.map((field) => field.localId === localId ? { ...field, ...patch } : field),
    }));
  }, []);

  const addField = useCallback(() => {
    setDraft((current) => {
      let index = current.fields.length + 1;
      let key = `field${index}`;
      const keys = new Set(current.fields.map((field) => field.key));
      while (keys.has(key)) {
        index += 1;
        key = `field${index}`;
      }
      return {
        ...current,
        fields: [...current.fields, {
          localId: crypto.randomUUID(),
          key,
          label: 'Neues Feld',
          type: 'text',
          unit: '',
          required: false,
          optionsText: '',
          active: true,
          group: '',
        }],
      };
    });
  }, []);

  const moveField = useCallback((localId: string, direction: -1 | 1) => {
    setDraft((current) => {
      const index = current.fields.findIndex((field) => field.localId === localId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.fields.length) return current;
      const fields = [...current.fields];
      [fields[index], fields[target]] = [fields[target], fields[index]];
      return { ...current, fields };
    });
  }, []);

  const removeField = useCallback((field: FieldDraft) => {
    setDraft((current) => ({
      ...current,
      fields: field.id
        ? current.fields.map((item) => item.localId === field.localId ? { ...item, active: false } : item)
        : current.fields.filter((item) => item.localId !== field.localId),
    }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const input = normalizeMachineTypeInput({
        tenantId,
        ...(draft.machineTypeId ? { machineTypeId: draft.machineTypeId } : {}),
        code: draft.code,
        name: draft.name,
        description: draft.description,
        active: draft.active,
        fields: draft.fields.map(toFieldInput),
      });
      const next = await gateway.saveMachineType(input);
      setCatalog(next);
      const saved = next.find((item) => item.id === draft.machineTypeId)
        ?? next.find((item) => item.code === input.code)
        ?? null;
      if (saved) {
        setSelectedId(saved.id);
        setDraft(draftFrom(saved));
      }
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [draft, gateway, tenantId]);

  return (
    <section className="admin-feature machine-types-feature">
      <div className="admin-feature__heading">
        <div>
          <span className="admin-eyebrow">Dynamische Stammdaten</span>
          <h2>Maschinentypen & technische Felder</h2>
          <p className="machine-types-lead">
            {tenantName}: Formulare werden aus diesen Schemas erzeugt und nicht fest in die Oberfläche programmiert.
          </p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void load()} disabled={loading || saving}>
          <RefreshCcw size={15} /> Neu laden
        </button>
      </div>

      {error ? <div className="admin-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError(null)}>Schließen</button></div> : null}

      {loading ? <div className="admin-loading">Maschinentypen werden geladen …</div> : (
        <div className="machine-types-workspace">
          <aside className="machine-types-list">
            <button
              type="button"
              className={selectedId === 'new' ? 'is-active is-new' : 'is-new'}
              onClick={() => { setSelectedId('new'); setDraft(blankDraft()); setError(null); }}
              disabled={!canManage}
            >
              <Plus size={17} />
              <span><strong>Neuer Maschinentyp</strong><small>Eigenes dynamisches Schema anlegen</small></span>
            </button>
            {catalog.map((item) => (
              <button
                type="button"
                key={item.id}
                className={selectedId === item.id ? 'is-active' : ''}
                onClick={() => selectRecord(item)}
              >
                <SlidersHorizontal size={17} />
                <span>
                  <strong>{item.name}</strong>
                  <small>{item.code} · {item.fields.filter((field) => field.active).length} Felder</small>
                </span>
                <em>{item.machineCount}</em>
                {!item.active ? <i>Inaktiv</i> : null}
              </button>
            ))}
          </aside>

          <div className="machine-types-editor">
            <div className="machine-types-editor__header">
              <div>
                <span className="admin-eyebrow">{draft.machineTypeId ? 'Schema bearbeiten' : 'Neues Schema'}</span>
                <h3>{draft.name || 'Maschinentyp ohne Namen'}</h3>
              </div>
              {selectedRecord?.machineCount ? (
                <span className="machine-types-usage"><LockKeyhole size={14} /> {selectedRecord.machineCount} Maschinen verwenden diesen Typ</span>
              ) : null}
            </div>

            <div className="machine-types-base-grid">
              <label>
                Typ-Code
                <input
                  value={draft.code}
                  onChange={(event) => updateDraft('code', event.target.value)}
                  placeholder="z. B. forklift"
                  disabled={!canManage}
                />
              </label>
              <label>
                Bezeichnung
                <input
                  value={draft.name}
                  onChange={(event) => updateDraft('name', event.target.value)}
                  placeholder="z. B. Gabelstapler"
                  disabled={!canManage}
                />
              </label>
              <label className="is-wide">
                Beschreibung
                <textarea
                  value={draft.description}
                  onChange={(event) => updateDraft('description', event.target.value)}
                  rows={2}
                  disabled={!canManage}
                />
              </label>
              <label className="machine-types-check">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(event) => updateDraft('active', event.target.checked)}
                  disabled={!canManage}
                />
                Maschinentyp aktiv
              </label>
            </div>

            <div className="machine-types-fields-heading">
              <div>
                <span className="admin-eyebrow">Formularfelder</span>
                <h4>Dynamische technische Daten</h4>
              </div>
              <button type="button" className="admin-secondary-button" onClick={addField} disabled={!canManage}>
                <Plus size={14} /> Feld hinzufügen
              </button>
            </div>

            <datalist id="machine-type-groups">
              {[...new Set(['Grunddaten', 'Technische Daten', 'Hubgerüst', 'Bereifung & Ausstattung',
                ...draft.fields.map((field) => field.group).filter(Boolean)])].map((group) => (
                <option key={group} value={group} />
              ))}
            </datalist>
            <div className="machine-types-fields">
              {draft.fields.map((field, index) => (
                <article key={field.localId} className={!field.active ? 'is-inactive' : ''}>
                  <div className="machine-types-field__order">
                    <button type="button" title="Nach oben" onClick={() => moveField(field.localId, -1)} disabled={!canManage || index === 0}><ArrowUp size={14} /></button>
                    <span>{index + 1}</span>
                    <button type="button" title="Nach unten" onClick={() => moveField(field.localId, 1)} disabled={!canManage || index === draft.fields.length - 1}><ArrowDown size={14} /></button>
                  </div>
                  <div className="machine-types-field__grid">
                    <label>Schlüssel<input value={field.key} onChange={(event) => updateField(field.localId, { key: event.target.value })} disabled={!canManage || !field.active} /></label>
                    <label>Bezeichnung<input value={field.label} onChange={(event) => updateField(field.localId, { label: event.target.value })} disabled={!canManage || !field.active} /></label>
                    <label>
                      Datentyp
                      <select value={field.type} onChange={(event) => updateField(field.localId, { type: event.target.value as MachineFieldType })} disabled={!canManage || !field.active}>
                        <option value="text">Text</option>
                        <option value="number">Zahl</option>
                        <option value="boolean">Ja / Nein</option>
                        <option value="date">Datum</option>
                        <option value="select">Auswahl</option>
                        <option value="multiselect">Mehrfachauswahl</option>
                      </select>
                    </label>
                    <label>Einheit<input value={field.unit} onChange={(event) => updateField(field.localId, { unit: event.target.value })} placeholder="kg, mm, h …" disabled={!canManage || !field.active} /></label>
                    <label>Gruppe / Schritt<input list="machine-type-groups" value={field.group} onChange={(event) => updateField(field.localId, { group: event.target.value })} placeholder="z.B. Technische Daten" disabled={!canManage || !field.active} /></label>
                    {field.type === 'select' || field.type === 'multiselect' ? (
                      <label className="is-wide">Auswahlwerte<input value={field.optionsText} onChange={(event) => updateField(field.localId, { optionsText: event.target.value })} placeholder="Elektro, Diesel, Treibgas" disabled={!canManage || !field.active} /></label>
                    ) : null}
                    <label className="machine-types-check">
                      <input type="checkbox" checked={field.required} onChange={(event) => updateField(field.localId, { required: event.target.checked })} disabled={!canManage || !field.active} />
                      Pflichtfeld
                    </label>
                  </div>
                  <div className="machine-types-field__actions">
                    {!field.active ? (
                      <button type="button" className="admin-secondary-button" onClick={() => updateField(field.localId, { active: true })} disabled={!canManage}>Wieder aktivieren</button>
                    ) : (
                      <button type="button" className="machine-types-delete" onClick={() => removeField(field)} disabled={!canManage}>
                        <Trash2 size={15} /> Entfernen
                      </button>
                    )}
                  </div>
                </article>
              ))}
              {draft.fields.length === 0 ? <p className="machine-types-empty">Noch keine technischen Felder. Der Typ kann trotzdem gespeichert und später erweitert werden.</p> : null}
            </div>

            <div className="machine-types-footer">
              <p>Bereits verwendete Feldschlüssel und Datentypen werden gegen zerstörerische Änderungen geschützt.</p>
              <button type="button" className="admin-primary-button" onClick={() => void save()} disabled={!canManage || saving}>
                <Save size={16} /> {saving ? 'Speichert …' : 'Maschinentyp speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
